import React, { useState, useEffect } from "react";
import {
  Table,
  Select,
  Input,
  Row,
  Col,
  Form,
  Typography,
  message,
  Button,
  Modal,
} from "antd";
import { v4 as uuidv4 } from "uuid";
import { platformApi } from "../../../../../api";
import { formatVehicleName } from "../../../../../utils/vehicleDisplayName";

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const AddVehicleTransferModal = ({ open, close, form, refreshData, createAccess = false }) => {
  const [branches, setBranch] = useState([]);
  const [vehicles, setVehicle] = useState([]);
  const [chassisNo, setchassisNo] = useState([]);
  const [challanData, setChallanData] = useState([]);
  const {
    getFieldDecorator,
    getFieldValue,
    resetFields,
    setFieldsValue,
  } = form;

  useEffect(() => {
    if (open) {
      platformApi.get("/api/user/branch").then((result) => {
        const { data } = result;
        if (data.code === 200) setBranch(data.data);
      });
      setChallanData([generateRow()]);
      resetFields();
    }
    // eslint-disable-next-line
  }, [open]);

  const generateRow = () => ({
    id: uuidv4(),
    model: null,
    color: null,
    colorId: null,
    chassisNo: null,
    chassisKey: null,
  });

  const getChassisNumber = (vehicle, colorId, rowId) => {
    const fromBranch = getFieldValue("fromBranch");
    
    if (!colorId || !fromBranch || !vehicle) {
      message.warning("Please ensure branch and color are selected");
      return;
    }

    const payload = {
      color: colorId,
      branch: fromBranch,
      vehicle: vehicle,
    };

    platformApi
      .post(`/api/vehicleInventory/getvehicles`, payload)
      .then((res) => {
        const options = res.data.response.data;
        if (options.length < 1) {
          message.info("No chassis number available in inventory");
          setchassisNo([]);
          return;
        }
        setchassisNo(options);
      })
      .catch((error) => {
        message.error("Error fetching chassis numbers:",error);
        setchassisNo([]);
      });
  };

  const columns = [
    {
      title: "Model",
      key: "model",
      width: 320,
      render: (text, record, index) => (
        <Select
          className="vehicle-select"
          value={record.model}
          onChange={(value) => handleModelChange(value, record.id)}
          placeholder="Model"
          showSearch
          style={{ width: "100%" }}
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 380 }}
          filterOption={(input, option) => {
            if (!option || !option.props || !option.props.children)
              return false;
            const vehicle = vehicles.find((v) => v.vehicle === option.key);
            if (!vehicle) return false;
            const modelCode = vehicle.modelCode
              ? vehicle.modelCode.toString().toLowerCase()
              : "";
            const modelName = vehicle.modelName
              ? vehicle.modelName.toString().toLowerCase()
              : "";
            const search = input.toLowerCase();
            return modelCode.includes(search) || modelName.includes(search);
          }}
        >
          {vehicles.map((vehicle) => (
            <Option
              key={vehicle.vehicle}
              value={vehicle.vehicle}
              title={formatVehicleName(vehicle)}
            >
              {formatVehicleName(vehicle)}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Color",
      key: "color",
      width: 140,
      render: (text, record) => {
        const selectedVehicle = vehicles.find((v) => v.vehicle === record.model);
        const colorOptions = selectedVehicle?.color || [];

        return (
          <Select
            value={record.colorId}
            onChange={(value) => handleColorChange(value, record.id)}
            placeholder="Color"
            disabled={!record.model}
          >
            {colorOptions.map((colorData) => (
              <Option key={colorData.id} value={colorData.id}>
                {colorData.color}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Chassis No",
      key: "chassisNo",
      render: (text, record) => (
        <Select
          value={record.chassisNo}
          onChange={(value, option) =>
            handleChassisChange(value, record.id, option.key)
          }
          placeholder="Chassis No"
          disabled={!record.colorId}
        >
          {chassisNo
            .filter(
              (c) => !challanData.some((d) => d.chassisNo === c.chassisNo)
            )
            .map((data) => (
              <Option key={data.id} value={data.chassisNo}>
                {data.chassisNo}
              </Option>
            ))}
        </Select>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Button
          type="danger"
          icon="delete"
          onClick={() => handleDelete(record.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  const handleModelChange = (value, rowId) => {
    setChallanData((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...generateRow(),
              id: rowId,
              model: value,
            }
          : row
      )
    );
    // Clear chassis numbers when model changes
    setchassisNo([]);
  };

  const handleColorChange = (colorId, rowId) => {
    const row = challanData.find((r) => r.id === rowId);
    const selectedVehicle = vehicles.find((v) => v.vehicle === row.model);
    const selectedColor = selectedVehicle?.color.find((c) => c.id === colorId);

    setChallanData((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              colorId: colorId,
              color: selectedColor?.color || null,
              chassisNo: null,
              chassisKey: null,
            }
          : r
      )
    );

    // Fetch chassis numbers for selected vehicle and color
    getChassisNumber(row.model, colorId, rowId);
  };

  const handleChassisChange = (value, rowId, chassisKey) => {
    setChallanData((prev) => {
      const updated = prev.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            chassisNo: value,
            chassisKey,
          };
        }
        return row;
      });
      if (updated.every((r) => r.chassisNo)) {
        return [...updated, generateRow()];
      }
      return updated;
    });
  };

  const handleDelete = (rowId) => {
    setChallanData((prev) => {
      const filtered = prev.filter((row) => row.id !== rowId);
      return filtered.length === 0 ? [generateRow()] : filtered;
    });
  };

  const handleFromBranchChange = (value) => {
    setFieldsValue({ toBranch: null });
    platformApi
      .post(`api/inventoryTransfers/vehicles`, { branch: value })
      .then((result) => {
        const { data } = result;
        if (data.code === 200) setVehicle(data.data);
      });
    setChallanData([generateRow()]);
    setchassisNo([]);
  };

  const handleSubmit = () => {
    if (!createAccess) {
      message.error("You do not have permission to submit vehicle transfers.");
      return;
    }
    const toBranch = getFieldValue("toBranch");
    const fromBranch = getFieldValue("fromBranch");
    const validData = challanData.filter((row) => row.model && row.chassisNo);
    if (!toBranch || !fromBranch) {
      message.error("Please select both branches!");
      return;
    }
    if (validData.length === 0) {
      message.error("Please add at least one valid vehicle entry!");
      return;
    }
    const payload = {
      vehicles: validData.map((row) => row.chassisKey),
      to_branch: toBranch,
      from_branch: fromBranch,
    };
    platformApi
      .put(`/api/inventoryTransfers/vehicles`, payload)
      .then((result) => {
        if (result.status === 200) {
          message.success("Transfer successful!");
          setChallanData([generateRow()]);
          resetFields();
          refreshData();
          close();
        }
      })
      .catch((error) => {
        message.error(`Transfer failed: ${error.message}`);
      });
  };

  return (
    <Modal
      title="Vehicle Transfer"
      visible={open}
      onCancel={close}
      footer={null}
      width={980}
      destroyOnClose
    >
      <Form>
        <Row gutter={30}>
          <Col span={8}>
            <Form.Item label="From Branch">
              {getFieldDecorator("fromBranch", {
                rules: [{ required: true }],
              })(
                <Select
                  placeholder="Select branch"
                  onChange={handleFromBranchChange}
                >
                  {branches.map((branch) => (
                    <Option key={branch.id} value={branch.id}>
                      {branch.name}
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label="To Branch">
              {getFieldDecorator("toBranch", {
                rules: [{ required: true }],
              })(
                <Select
                  placeholder="Select branch"
                  disabled={!getFieldValue("fromBranch")}
                >
                  {branches
                    .filter((b) => b.id !== getFieldValue("fromBranch"))
                    .map((branch) => (
                      <Option key={branch.id} value={branch.id}>
                        {branch.name}
                      </Option>
                    ))}
                </Select>
              )}
            </Form.Item>
          </Col>
        </Row>
        <Table
          size="small"
          pagination={false}
          scroll={{ y: 300 }}
          columns={columns}
          dataSource={challanData}
          rowKey="id"
          bordered
        />
        <Button
          type="primary"
          style={{ marginTop: 16 }}
          onClick={handleSubmit}
          disabled={!createAccess || !getFieldValue("fromBranch") || !getFieldValue("toBranch")}
        >
          Submit Transfer
        </Button>
      </Form>
    </Modal>
  );
};

export default Form.create()(AddVehicleTransferModal);
